import React, { useState, useEffect, useMemo } from "react";
import { Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

import {
  getAdminPanelCountsAPI,
  getCompanyData,
} from "../../Service/apiService";
import "../../assets/css/Analytics.css";

import "react-loading-skeleton/dist/skeleton.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,

);

const toLower = (val) => (val || "").toLowerCase().trim();
const parseDateSafe = (str) => {
  if (!str) return null;
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

const CATEGORIES = [
  "uniqueLeads",
  "freeTrial",
  "trialFull",
  "requestDemo",
  "estimatedPricing",
  "hsnPrice",
  "apiPrice",
  "resourcesReport",
  "emailSubscribers",
  "leoshServices",
  "eventLeads",
];

const LABEL_MAP = {
  uniqueLeads: "Unique Leads",
  freeTrial: "Free Access Trial",
  trialFull: "Trial Full Access",
  requestDemo: "Request Demo",
  estimatedPricing: "Estimated Pricing",
  hsnPrice: "HSN Code Avg. Price",
  apiPrice: "API Calculator Avg. Price",
  resourcesReport: "Resources and Report",
  emailSubscribers: "Email Subscribers",
  leoshServices: "Leosh Services",
  eventLeads: "Event Leads",
};

const COLORS = {
  uniqueLeads: "rgb(42, 86, 130)",
  freeTrial: "rgba(60, 122, 185, 1)",
  trialFull: "rgba(31, 67, 104, 1)",
  requestDemo: "rgba(39, 72, 105, 1)",
  estimatedPricing: "rgba(99, 150, 200, 1)",
  hsnPrice: "rgba(79, 141, 202, 1)",
  apiPrice: "rgba(97, 134, 171, 1)",
  resourcesReport: "rgba(45, 84, 122, 1)",
  emailSubscribers: "rgba(60, 110, 159, 1)",
  leoshServices: "rgba(37, 60, 84, 1)",
  eventLeads: "rgba(97, 134, 171, 1)", // Fixed typo: was "#rgba(...)"
};

const DATE_FIELD = {
  uniqueLeads: "createdAt",
  freeTrial: "createdAt",
  trialFull: "createdAt",
  requestDemo: "createdAt",
  estimatedPricing: "createdAt",
  hsnPrice: "createdAt",
  apiPrice: "createdAt",
  resourcesReport: "createdAt",
  emailSubscribers: "createdAt",
  leoshServices: "leadsDate",
  eventLeads: "createdAt",
};

// Helper: build time buckets (unchanged)
const buildTimeSeries = (
  items,
  category,
  timeRange,
  startDate,
  isCompany = false
) => {
  const now = new Date();
  const granularity =
    timeRange === "1w" ? "day" : timeRange === "1m" ? "week" : "month";
  const buckets = [];
  // const clone = (d) => new Date(d.getTime());

  if (granularity === "day") {
    const cursor = new Date(startDate);
    while (cursor <= now) {
      const start = new Date(cursor);
      const end = new Date(cursor);
      end.setHours(23, 59, 59, 999);
      const key = start.toISOString().split("T")[0];
      const label = start.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      buckets.push({ start, end, key, label });
      cursor.setDate(cursor.getDate() + 1);
    }
  } else if (granularity === "week") {
    const sd = new Date(startDate);
    const day = sd.getDay();
    const diff = sd.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(sd);
    monday.setDate(diff);
    let cursor = new Date(monday);
    while (cursor <= now) {
      const start = new Date(cursor);
      start.setHours(0, 0, 0, 0);
      const end = new Date(cursor);
      end.setDate(end.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      const key = start.toISOString().split("T")[0];
      const endMonth = end.toLocaleDateString("en-US", { month: "short" });
      const label = `${start.getDate()}–${end.getDate()} ${endMonth}`;
      buckets.push({ start, end, key, label });
      cursor.setDate(cursor.getDate() + 7);
    }
  } else {
    const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    while (cursor <= now) {
      const start = new Date(
        cursor.getFullYear(),
        cursor.getMonth(),
        1,
        0,
        0,
        0,
        0
      );
      const end = new Date(
        cursor.getFullYear(),
        cursor.getMonth() + 1,
        0,
        23,
        59,
        59,
        999
      );
      const key = `${start.getFullYear()}-${String(
        start.getMonth() + 1
      ).padStart(2, "0")}`;
      const label = start.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
      });
      buckets.push({ start, end, key, label });
      cursor.setMonth(cursor.getMonth() + 1);
    }
  }

  const counts = buckets.map(() => 0);

  if (!items?.length) {
    return { labels: buckets.map((b) => b.label), values: counts };
  }

  const dateField = DATE_FIELD[category];
  if (!dateField)
    return { labels: buckets.map((b) => b.label), values: counts };

  items.forEach((item) => {
    let eventDate;
    if (isCompany) {
      eventDate = parseDateSafe(item.leadsDate);
    } else {
      eventDate = parseDateSafe(item[dateField] || item.createdAt);
    }
    if (!eventDate) return;
    buckets.forEach((b, idx) => {
      if (eventDate >= b.start && eventDate <= b.end) {
        counts[idx] += 1;
      }
    });
  });

  return { labels: buckets.map((b) => b.label), values: counts };
};

export default function LeadAnalytics() {
  // const navigate = useNavigate();
  // const location = useLocation();

  const [timeRange, setTimeRange] = useState("1y");
  // Default to first category: "uniqueLeads"
  const [selectedCategory, setSelectedCategory] = useState("uniqueLeads");
  const [loading, setLoading] = useState(true);

  const [leadData, setLeadData] = useState({});
  const [leoshCompanies, setLeoshCompanies] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const leadsRes = await getAdminPanelCountsAPI();
        const leads = leadsRes?.result || {};

        const companyRes = await getCompanyData("all");
        const companies = companyRes?.data || [];
        const nonInternal = companies.filter(
          (c) => toLower(c.companyType) !== "internal"
        );
        const leosh = nonInternal.filter((c) => c.leadsDate);

        setLeadData({
          uniqueLeads: Array.isArray(leads.totalUniqueLeads)
            ? leads.totalUniqueLeads
            : [],
          freeTrial: Array.isArray(leads.freeTrialUserData)
            ? leads.freeTrialUserData
            : [],
          requestDemo: Array.isArray(leads.demoUserData)
            ? leads.demoUserData
            : [],
          estimatedPricing: Array.isArray(leads.pricingUserData)
            ? leads.pricingUserData
            : [],
          hsnPrice: Array.isArray(leads.hsnUserData) ? leads.hsnUserData : [],
          apiPrice: Array.isArray(leads.apiCalculatorUserData)
            ? leads.apiCalculatorUserData
            : [],
          resourcesReport: Array.isArray(leads.resourceUserData)
            ? leads.resourceUserData
            : [],
          emailSubscribers: Array.isArray(leads.subscribedUserData)
            ? leads.subscribedUserData
            : [],
          eventLeads: Array.isArray(leads.eventData) ? leads.eventData : [],
        });
        setLeoshCompanies(leosh);
      } catch (err) {
        console.error("Error fetching lead data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const startDate = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const date = now.getDate();
    switch (timeRange) {
      case "1w":
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      case "1m":
        return new Date(year, month - 1, date);
      case "3m":
        return new Date(year, month - 3, date);
      case "6m":
        return new Date(year, month - 6, date);
      case "1y":
        return new Date(year - 1, month, date);
      default:
        return new Date(year, month - 3, date);
    }
  }, [timeRange]);

  //  Pie data: no "totalLeads"
  const pieData = useMemo(() => {
    const labels = CATEGORIES.map((cat) => LABEL_MAP[cat]);
    const data = CATEGORIES.map((cat) => {
      if (cat === "leoshServices") return leoshCompanies.length;
      if (cat === "trialFull") return 0; // hardcoded
      return leadData[cat]?.length || 0;
    });

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: CATEGORIES.map((cat) => COLORS[cat]),
          borderWidth: 1,
          borderColor: "#fff",
        },
      ],
    };
  }, [leadData, leoshCompanies]);

  // Time series data
  const timeChartData = useMemo(() => {
    let items = [];
    let isCompany = false;

    if (selectedCategory === "leoshServices") {
      items = leoshCompanies;
      isCompany = true;
    } else if (selectedCategory === "trialFull") {
      items = []; // no data
    } else {
      items = leadData[selectedCategory] || [];
    }

    const { labels, values } = buildTimeSeries(
      items,
      selectedCategory,
      timeRange,
      startDate,
      isCompany
    );

    return {
      labels,
      datasets: [
        {
          label: LABEL_MAP[selectedCategory] || "Leads",
          data: values,
          borderColor: COLORS[selectedCategory] || "#333",
          backgroundColor: `${(COLORS[selectedCategory] || "#333").replace(
            "#",
            ""
          )}25`,
          tension: 0.3,
          pointBackgroundColor: COLORS[selectedCategory] || "#333",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
          fill: true,
        },
      ],
    };
  }, [selectedCategory, timeRange, startDate, leadData, leoshCompanies]);

  const handlePieClick = (elements) => {
    if (elements.length > 0) {
      const idx = elements[0].index;
      setSelectedCategory(CATEGORIES[idx]);
    }
  };

  // const handleBack = () => {
  //   const dashboardPath = location.pathname.replace("/analytics", "/dashboard");
  //   navigate(dashboardPath);
  // };

  const timeOptions = [
    { label: "1 Week", value: "1w" },
    { label: "1 Month", value: "1m" },
    { label: "3 Months", value: "3m" },
    { label: "6 Months", value: "6m" },
    { label: "1 Year", value: "1y" },
  ];

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { font: { size: 12 }, color: "#333" },
      },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "#2a5682",
        bodyColor: "#333",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
      },
    },
    onClick: (_evt, elements) => handlePieClick(elements),
    onHover: (evt, elements) => {
      evt.native.target.style.cursor =
        elements.length > 0 ? "pointer" : "default";
    },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: { font: { size: 12 }, color: "#333" },
      },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "#2a5682",
        bodyColor: "#333",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(0,0,0,0.05)" },
        ticks: { color: "#666", font: { size: 11 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.05)" },
        ticks: { color: "#666", font: { size: 11 } },
        title: {
          display: true,
          text: "Leads",
          color: "#333",
          font: { size: 12, weight: "600" },
        },
      },
    },
  };

  return (
    <div className="analytics-container">
      <div className="header-section">
        <h1 className="title">Lead Analytics</h1>
        <div className="time-filter">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            {timeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading lead data...</div>
      ) : (
        <div className="analytics-content">
          <div className="chart-panel">
            <h3>Lead Distribution</h3>
            <div className="chart-wrapper">
              <Pie data={pieData} options={pieOptions} />
            </div>
          </div>

          <div className="chart-panel">
            <h3>Trend: {LABEL_MAP[selectedCategory] || "Leads"}</h3>
            <div className="chart-wrapper">
              {timeChartData.labels.length > 0 &&
              timeChartData.datasets[0].data.some((v) => v > 0) ? (
                <Line data={timeChartData} options={lineOptions} />
              ) : (
                <div className="no-data">No data in selected period</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
